'use client';

import React, { useContext, useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { getCookie } from 'cookies-next';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { getApiKeys } from '@/helpers/utils/siteUtils';
import { ProductContext } from './../../contexts';
import { ProjectSettings } from '../../../../../../../../types/projectSettings/ProjectSettings';
import { COOKIE_KEYS } from '../../../../../../../constants/constant';
import StarFill from '../../../../../../customercenter-ui/components/icons/startFill';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../../../../customercenter-ui/components/ui/accordion';

declare global {
  interface Window {
    pwrData: any;
  }
}

const PowerReviews = () => {
  const [activeAccordion, setActiveAccordion] = useState<string | null>('item-1');
  const qaRef = useRef<HTMLDivElement>(null);
  const { initialColor } = useContext(ProductContext);
  const projectSettings: ProjectSettings = useProjSettings();
  const settings = projectSettings?.powerReviewsSettings;
  const pathname = usePathname();
  const [siteSpecificData, setSiteSpecificData] = useState<any>();
  const urlSKU = pathname
    .substring(0, pathname.length - 1)
    .substring(pathname.substring(0, pathname.length - 1).lastIndexOf('/') + 1);

  const initPWR = (containers: string) => {
    const containersArray = containers.split(',');
    const components = containersArray.includes('item-1')
      ? { ReviewDisplay: 'pr-reviewdisplay', ReviewSnippet: 'pr-temp' }
      : containersArray.includes('item-2')
      ? { QuestionDisplay: 'pr-q-a' }
      : {};
    const pwr: any = 'pwr' in window ? window.pwr : () => {};
    pwr('render', {
      api_key: siteSpecificData?.apiKey,
      locale: settings?.locale,
      merchant_group_id: siteSpecificData?.merchantGroupId,
      merchant_id: siteSpecificData?.merchantId,
      page_id: urlSKU !== '' ? urlSKU.split('-')[0] : urlSKU,
      components: components,
      on_render: (config: any, data: any) => {
        window.pwrData = data;
      },
    });
  };

  useEffect(() => {
    const cookieSiteId = getCookie(COOKIE_KEYS.selectedSite) ?? 'ZBSite';
    const siteId = cookieSiteId?.split('Site')?.[0] ?? 'ZB';
    const siteSpecificData = getApiKeys(settings, siteId) as any;
    setSiteSpecificData(siteSpecificData);
  }, []);

  useEffect(() => {
    if (!document.getElementById('pwrScript')) {
      const scriptTag = document.createElement('script');
      scriptTag.src = 'https://ui.powerreviews.com/stable/4.1/ui.js';
      scriptTag.async = true;
      scriptTag.id = 'pwrScript';
      document.body.appendChild(scriptTag);
    }
    if (siteSpecificData) {
      const pwrTimeInterval = setInterval(() => {
        if ('pwr' in window && siteSpecificData && siteSpecificData?.apiKey) {
          initPWR('item-1');
          clearInterval(pwrTimeInterval);
        }
      }, 1000);
    }
  }, [settings, siteSpecificData]);

  const accordionChanged = (value: string) => {
    if (value !== '') {
      initPWR(value);
    }
    if (value === 'item-2' && activeAccordion === 'item-1' && qaRef.current) {
      qaRef.current?.scrollIntoView({
        behavior: 'instant',
        block: 'center',
      });
    }
    setActiveAccordion(value);
  };

  return (
    <section className="mx-auto max-w-[1390px]">
      <section className="relative w-[1016px]">
        <section className="mb-5 flex items-center justify-between">
          <Accordion type="single" onValueChange={accordionChanged} collapsible className="w-full">
            <AccordionItem ref={qaRef} value="item-1" className="border-0">
              <AccordionTrigger className="border-b text-2xl font-bold leading-6">
                {typeof window !== 'undefined' && (
                  <section className="flex gap-x-0.5">
                    <span className="mr-2">Reviews</span>
                    <StarFill value={window.pwrData?.average_rating} count={5} productId={initialColor?.key} />
                    <p className="pl-2.5 text-sm font-normal leading-5">({window.pwrData?.review_count} Reviews)</p>
                  </section>
                )}
              </AccordionTrigger>
              <AccordionContent className="pt-4">
                <div id="pr-reviewdisplay"></div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
          <div id="pr-temp" className="fixed -left-[9999px] -top-[9999px]"></div>
        </section>
      </section>
    </section>
  );
};

export default PowerReviews;
